import { useCallback, useMemo, useState } from "react";
import { SELECTABLE_SELECTOR } from "../utils/selectable";

export type FileSelection = {
  /** The currently-selected uids. */
  selectedUids: Set<string>;
  /** Convenience count (`selectedUids.size`). */
  selectedCount: number;
  /** Whether a given uid is selected. */
  isSelected: (uid: string) => boolean;
  /** Toggle a single uid in/out of the selection. */
  toggle: (uid: string) => void;
  /** Clear the selection (idempotent — keeps the same Set when already empty). */
  clear: () => void;
  /** Select every uid passed to the hook. */
  selectAll: () => void;
  /** Replace the whole selection — used as the marquee's `onSelect`. */
  setSelection: (uids: Set<string>) => void;
  /**
   * Click-anywhere-to-deselect (file-manager behaviour): a click whose target
   * isn't a selectable item, button or link clears the selection. Items open
   * their own action (preview / navigate); buttons/links keep theirs (clearing
   * on, say, select-all would undo it). Spread onto the region wrapper's
   * `onClick`. Post-drag clicks are expected to be swallowed upstream by the
   * marquee's `onClickCapture`, so only genuine clicks reach here.
   */
  handleBackgroundClick: (e: React.MouseEvent) => void;
};

/**
 * Selection state for a list of selectable file-browser items, decoupled from
 * any data source or API. Owns the selected-uid `Set` and the mutators shared by
 * the share-link viewer and the published-space page.
 *
 * Pair it with {@link useMarqueeSelection} (feed `setSelection` as `onSelect`),
 * {@link useSelectionKeyboard} (wire `selectAll`/`clear`) and the
 * `handleBackgroundClick` handler on the region wrapper.
 *
 * @param allUids Every selectable uid, in display order — the universe for
 *   `selectAll`. Pass a stable/memoized array to keep `selectAll` referentially
 *   stable.
 */
export function useFileSelection(allUids: string[]): FileSelection {
  const [selectedUids, setSelectedUids] = useState<Set<string>>(new Set());

  const isSelected = useCallback(
    (uid: string) => selectedUids.has(uid),
    [selectedUids]
  );

  const toggle = useCallback((uid: string) => {
    setSelectedUids((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    // Idempotent: keep the same Set when already empty so a background click
    // with nothing selected doesn't trigger a needless re-render.
    setSelectedUids((prev) => (prev.size === 0 ? prev : new Set()));
  }, []);

  const selectAll = useCallback(() => {
    setSelectedUids(new Set(allUids));
  }, [allUids]);

  const setSelection = useCallback((uids: Set<string>) => {
    setSelectedUids(uids);
  }, []);

  const handleBackgroundClick = useCallback(
    (e: React.MouseEvent) => {
      if (
        (e.target as HTMLElement).closest(`${SELECTABLE_SELECTOR}, button, a`)
      ) {
        return;
      }
      clear();
    },
    [clear]
  );

  return useMemo(
    () => ({
      selectedUids,
      selectedCount: selectedUids.size,
      isSelected,
      toggle,
      clear,
      selectAll,
      setSelection,
      handleBackgroundClick,
    }),
    [
      selectedUids,
      isSelected,
      toggle,
      clear,
      selectAll,
      setSelection,
      handleBackgroundClick,
    ]
  );
}
